import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { createQuery } from '@shared/lib/query-helpers';
import type { operations } from '@molchanovs/api-contract';
import type { UserProfilesDirectorySort } from '../model';

export const userProfilesPageSize = 10 as const;
const minOrdinaryTextSearchLength = 2;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const numericIdentifierPattern = /^\d+$/u;

/** Canonical generated response for GET /api/v1/user-profiles. */
export type UserProfilesResponse =
  operations['getApiV1UserProfiles']['responses'][200]['content']['application/json'];

type UserProfilesRequestQuery = NonNullable<
  operations['getApiV1UserProfiles']['parameters']['query']
>;
type CombinedSearchRequestQuery = NonNullable<
  operations['getApiV1CrmSearch']['parameters']['query']
>;

type UserProfilesSortParameter = NonNullable<UserProfilesRequestQuery['sort']>;

export type UserProfilesFilters = Pick<
  UserProfilesRequestQuery,
  | 'accountLinkage'
  | 'businessLineKeys'
  | 'certificationTypeIds'
  | 'cities'
  | 'currentMembershipStatuses'
  | 'freshnessStates'
  | 'hasDocuments'
  | 'hasShopifyCustomerId'
  | 'linkedSystems'
  | 'membershipTypeIds'
  | 'profileOrigins'
  | 'shopifyCustomerId'
>;

type UserProfileItem = UserProfilesResponse['data']['items'][number];

const activityAvailableCounterSchema = z
  .object({ state: z.literal('available'), value: z.number().int().nonnegative() })
  .strict();

const activityStoredValueUnavailableReasonSchema = z.enum([
  'storedValueMissing',
  'storedValueInvalid',
]);

const activityRegistrationCounterSchema = z.union([
  activityAvailableCounterSchema,
  z
    .object({ reason: activityStoredValueUnavailableReasonSchema, state: z.literal('unavailable') })
    .strict(),
]);

const activityLearningStoredValueCounterSchema = z.union([
  activityAvailableCounterSchema,
  z
    .object({
      reason: z.union([z.literal('notAuthorized'), activityStoredValueUnavailableReasonSchema]),
      state: z.literal('unavailable'),
    })
    .strict(),
]);

const activityLearningSourceListCounterSchema = z.union([
  activityAvailableCounterSchema,
  z
    .object({
      reason: z.union([z.literal('notAuthorized'), z.literal('sourceListMissing')]),
      state: z.literal('unavailable'),
    })
    .strict(),
]);

const activitySchoolSchema = z.union([
  z.object({ state: z.literal('available'), value: z.string().nullable() }).strict(),
  z
    .object({
      issues: z
        .array(
          z.enum([
            'organizationListMissing',
            'organizationReferenceUnresolved',
            'organizationNameMissing',
            'mainLocationReferenceUnresolved',
            'organizationCityMissing',
            'mainLocationEnglishNameMissing',
          ])
        )
        .min(1),
      state: z.literal('unavailable'),
    })
    .strict(),
]);

const activitySchema = z
  .object({
    btPrograms: activityLearningStoredValueCounterSchema,
    certificates: activityLearningSourceListCounterSchema,
    courses: activityLearningSourceListCounterSchema,
    registrations: z
      .object({
        allTime: activityRegistrationCounterSchema,
        halfYear: activityRegistrationCounterSchema,
        month: activityRegistrationCounterSchema,
        quarter: activityRegistrationCounterSchema,
        week: activityRegistrationCounterSchema,
        year: activityRegistrationCounterSchema,
      })
      .strict(),
    school: activitySchoolSchema,
    workouts: activityLearningStoredValueCounterSchema,
  })
  .strict();

const userProfileItemSchema: z.ZodType<UserProfileItem> = z
  .object({
    activity: activitySchema,
    birthDate: z.iso.datetime().nullable(),
    city: z.string().nullable(),
    currentMembership: z
      .object({
        expiresAt: z.iso.datetime().nullable(),
        number: z.string().nullable(),
        status: z.string().nullable(),
        type: z.object({ id: z.uuid().nullable(), name: z.string().nullable() }).strict(),
      })
      .strict()
      .nullable(),
    country: z.string().nullable(),
    discounts: z.object({ highest: z.number().nullable(), volume: z.number().nullable() }).strict(),
    freediverId: z.string().nullable(),
    freshnessState: z.enum(['fresh', 'stale', 'unknown']),
    id: z.uuid(),
    inductingInstructor: z
      .object({ displayName: z.string().nullable(), id: z.uuid() })
      .strict()
      .nullable(),
    instructorId: z.string().nullable(),
    lastCertification: z
      .object({
        certifiedAt: z.iso.datetime(),
        type: z.object({ id: z.uuid(), label: z.string().nullable() }).strict(),
      })
      .strict()
      .nullable(),
    lastDocument: z
      .object({
        status: z.object({ id: z.uuid().nullable(), label: z.string().nullable() }).strict(),
        type: z.object({ id: z.uuid().nullable(), label: z.string().nullable() }).strict(),
        validUntil: z.iso.datetime().nullable(),
      })
      .strict()
      .nullable(),
    personalAddress: z.string().nullable(),
    preferredLanguage: z.string().nullable(),
    registrationsCount: z.number().nullable(),
    roles: z.array(z.string()),
    shopify: z.object({ id: z.string().nullable(), tags: z.array(z.string()) }).strict(),
    submittingInstructor: z
      .object({ displayName: z.string().nullable(), id: z.uuid() })
      .strict()
      .nullable(),
    updatedAt: z.iso.datetime().nullable(),
    user: z
      .object({
        avatarUrl: z.url().nullable(),
        displayName: z.string().min(1),
        email: z.string().nullable(),
      })
      .strict(),
    userOrigin: z.enum(['Shopify', 'Move']).nullable(),
  })
  .strip();

export const userProfilesResponseSchema: z.ZodType<UserProfilesResponse> = z
  .object({
    data: z.object({ items: z.array(userProfileItemSchema).max(userProfilesPageSize) }).strip(),
    meta: z
      .object({
        countAsOf: z.iso.datetime().nullable(),
        countState: z.enum(['exact', 'pending', 'unavailable']),
        hasNextPage: z.boolean(),
        hasPreviousPage: z.boolean(),
        page: z.number().int().positive(),
        pageSize: z.literal(userProfilesPageSize),
        totalItems: z.number().int().nonnegative().nullable(),
        totalPages: z.number().int().nonnegative().nullable(),
      })
      .strip(),
  })
  .strip();

export function normalizeUserProfilesSearchInput(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

export function normalizeUserProfilesSearch(value: string | undefined) {
  const normalized = normalizeUserProfilesSearchInput(value);
  if (normalized === undefined) return undefined;
  return normalized.length >= minOrdinaryTextSearchLength ||
    uuidPattern.test(normalized) ||
    numericIdentifierPattern.test(normalized)
    ? normalized
    : undefined;
}

export function toUserProfilesSortParameter(
  sort: UserProfilesDirectorySort | undefined
): UserProfilesSortParameter | undefined {
  if (sort === undefined) return undefined;
  const [key, direction] = sort.split('.') as [string, 'asc' | 'desc'];
  return (direction === 'asc' ? [key, 'id'] : [`-${key}`, '-id']) as UserProfilesSortParameter;
}

export function normalizeUserProfilesRoles(values: readonly string[] | undefined) {
  if (!values) return undefined;

  const normalized = [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right)
  );
  return normalized.length > 0 ? normalized : undefined;
}

function normalizeUserProfilesFilterValues(values: readonly string[] | undefined) {
  if (values === undefined) return undefined;
  const normalized = [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right)
  );
  return normalized.length > 0 ? normalized : undefined;
}

function normalizeUserProfilesShopifyCustomerId(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

export function normalizeUserProfilesFilters(
  filters: UserProfilesFilters | undefined
): UserProfilesFilters {
  return {
    ...(filters?.accountLinkage !== undefined ? { accountLinkage: filters.accountLinkage } : {}),
    ...(normalizeUserProfilesFilterValues(filters?.businessLineKeys) !== undefined
      ? { businessLineKeys: normalizeUserProfilesFilterValues(filters?.businessLineKeys) }
      : {}),
    ...(normalizeUserProfilesFilterValues(filters?.certificationTypeIds) !== undefined
      ? { certificationTypeIds: normalizeUserProfilesFilterValues(filters?.certificationTypeIds) }
      : {}),
    ...(normalizeUserProfilesFilterValues(filters?.cities) !== undefined
      ? { cities: normalizeUserProfilesFilterValues(filters?.cities) }
      : {}),
    ...(normalizeUserProfilesFilterValues(filters?.currentMembershipStatuses) !== undefined
      ? {
          currentMembershipStatuses: normalizeUserProfilesFilterValues(
            filters?.currentMembershipStatuses
          ),
        }
      : {}),
    ...(normalizeUserProfilesFilterValues(filters?.freshnessStates) !== undefined
      ? { freshnessStates: normalizeUserProfilesFilterValues(filters?.freshnessStates) }
      : {}),
    ...(filters?.hasDocuments === true ? { hasDocuments: true } : {}),
    ...(filters?.hasShopifyCustomerId === true ? { hasShopifyCustomerId: true } : {}),
    ...(normalizeUserProfilesFilterValues(filters?.linkedSystems) !== undefined
      ? { linkedSystems: normalizeUserProfilesFilterValues(filters?.linkedSystems) }
      : {}),
    ...(normalizeUserProfilesFilterValues(filters?.membershipTypeIds) !== undefined
      ? { membershipTypeIds: normalizeUserProfilesFilterValues(filters?.membershipTypeIds) }
      : {}),
    ...(normalizeUserProfilesFilterValues(filters?.profileOrigins) !== undefined
      ? { profileOrigins: normalizeUserProfilesFilterValues(filters?.profileOrigins) }
      : {}),
    ...(normalizeUserProfilesShopifyCustomerId(filters?.shopifyCustomerId) !== undefined
      ? { shopifyCustomerId: normalizeUserProfilesShopifyCustomerId(filters?.shopifyCustomerId) }
      : {}),
  } as UserProfilesFilters;
}

export const userProfilesKeys = {
  all: ['user-profiles'] as const,
  list: (
    page: number,
    viewerAccessScope: string,
    q?: string,
    roles?: readonly string[],
    hasCurrentInstructorMembership = false,
    filters?: UserProfilesFilters,
    sort?: UserProfilesDirectorySort,
    combinedSearch = false
  ) =>
    [
      ...userProfilesKeys.all,
      combinedSearch && normalizeUserProfilesSearch(q) ? 'combined-search' : 'browse',
      viewerAccessScope,
      {
        page,
        pageSize: userProfilesPageSize,
        q: normalizeUserProfilesSearch(q),
        roles: normalizeUserProfilesRoles(roles) ?? [],
        hasCurrentInstructorMembership,
        filters: normalizeUserProfilesFilters(filters),
        sort:
          toUserProfilesSortParameter(
            sort ??
              (combinedSearch && normalizeUserProfilesSearch(q) ? 'displayName.asc' : undefined)
          ) ?? [],
      },
    ] as const,
};

export function userProfilesQuery(
  page: number,
  viewerAccessScope: string,
  q?: string,
  roles?: readonly string[],
  hasCurrentInstructorMembership = false,
  filters?: UserProfilesFilters,
  sort?: UserProfilesDirectorySort,
  combinedSearch = false
) {
  const normalizedQ = normalizeUserProfilesSearch(q);
  const normalizedRoles = normalizeUserProfilesRoles(roles);
  const normalizedFilters = normalizeUserProfilesFilters(filters);
  const useCombinedSearch = combinedSearch && normalizedQ !== undefined;
  const sortParameter = toUserProfilesSortParameter(
    sort ?? (useCombinedSearch ? 'displayName.asc' : undefined)
  );
  const commonParams = {
    page,
    pageSize: userProfilesPageSize,
    ...(normalizedQ ? { q: normalizedQ } : {}),
    ...(normalizedRoles ? { roles: normalizedRoles } : {}),
    ...(hasCurrentInstructorMembership ? { hasCurrentInstructorMembership: true as const } : {}),
    ...normalizedFilters,
    ...(sortParameter !== undefined ? { sort: sortParameter } : {}),
  };
  const queryKey = userProfilesKeys.list(
    page,
    viewerAccessScope,
    normalizedQ,
    normalizedRoles,
    hasCurrentInstructorMembership,
    normalizedFilters,
    sort,
    combinedSearch
  );
  if (useCombinedSearch) {
    const params: CombinedSearchRequestQuery = { ...commonParams, q: normalizedQ, count: 'exact' };
    return createQuery<UserProfilesResponse>({
      path: '/api/v1/crm/search',
      queryKey,
      requestOptions: { params },
      schema: userProfilesResponseSchema,
    });
  }
  const params: UserProfilesRequestQuery = commonParams;
  return createQuery<UserProfilesResponse>({
    path: '/api/v1/user-profiles',
    queryKey,
    requestOptions: { params },
    schema: userProfilesResponseSchema,
  });
}

type PreviousUserProfilesQuery = { queryKey: readonly unknown[] };

export function keepPreviousUserProfilesDataForViewer<T>(viewerAccessScope: string) {
  return (previousData: T | undefined, previousQuery: PreviousUserProfilesQuery | undefined) =>
    viewerAccessScope.length > 0 && previousQuery?.queryKey[2] === viewerAccessScope
      ? previousData
      : undefined;
}

/**
 * Counts are delivered in the User Profiles collection response. Poll only while the
 * backend has a filtered exact count in flight; an exact or unavailable result is terminal
 * for this observer and must not schedule another automatic refetch.
 */
export function userProfilesCountRefreshInterval(
  countState: UserProfilesResponse['meta']['countState'] | undefined
) {
  // A refetch repeats the page query because the collection owns both rows and
  // the count contract. Give the background exact count time to complete before
  // asking for its stored result instead of issuing a second full page query each
  // second.
  return countState === 'pending' ? 2_500 : false;
}

export function useUserProfiles(
  page: number,
  viewerAccessScope: string,
  q?: string,
  roles?: readonly string[],
  hasCurrentInstructorMembership = false,
  options?: {
    combinedSearch?: boolean;
    enabled?: boolean;
    filters?: UserProfilesFilters;
    sort?: UserProfilesDirectorySort;
  }
) {
  return useQuery({
    ...userProfilesQuery(
      page,
      viewerAccessScope,
      q,
      roles,
      hasCurrentInstructorMembership,
      options?.filters,
      options?.sort,
      options?.combinedSearch
    ),
    enabled: viewerAccessScope.length > 0 && (options?.enabled ?? true),
    gcTime: 5 * 60 * 1000,
    refetchInterval: (query) => userProfilesCountRefreshInterval(query.state.data?.meta.countState),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 30_000,
  });
}
